/**
 * Estate Flow - Signup Page with Referral Support
 * Copyright © 2025 Sebastian Alliance Group LLC. All Rights Reserved.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Building2, Loader2, Gift } from 'lucide-react';
import ExitIntentPopup from './ExitIntentPopup';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CARIBBEAN_COUNTRIES = [
  { name: 'Antigua and Barbuda', code: 'AG' },
  { name: 'Bahamas', code: 'BS' },
  { name: 'Barbados', code: 'BB' },
  { name: 'Belize', code: 'BZ' },
  { name: 'Cuba', code: 'CU' },
  { name: 'Dominica', code: 'DM' },
  { name: 'Dominican Republic', code: 'DO' },
  { name: 'Grenada', code: 'GD' },
  { name: 'Guyana', code: 'GY' },
  { name: 'Haiti', code: 'HT' },
  { name: 'Jamaica', code: 'JM' },
  { name: 'Saint Kitts and Nevis', code: 'KN' },
  { name: 'Saint Lucia', code: 'LC' },
  { name: 'Saint Vincent and the Grenadines', code: 'VC' },
  { name: 'Suriname', code: 'SR' },
  { name: 'Trinidad and Tobago', code: 'TT' },
  { name: 'Aruba', code: 'AW' },
  { name: 'Curaçao', code: 'CW' },
  { name: 'Sint Maarten', code: 'SX' }
];

const SignupPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [referralCode, setReferralCode] = useState('');
  
  // Check ref code directly from URL (not state) to prevent flash
  const urlRefCode = searchParams.get('ref');
  const hasReferralCode = !!urlRefCode;
  
  // Get initial role from URL
  const initialRole = searchParams.get('role') || '';
  
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    phone_number: '',
    role: initialRole,
    country: ''
  });

  // Capture referral code and role from URL query parameters
  useEffect(() => {
    const refCode = searchParams.get('ref');
    const roleParam = searchParams.get('role');
    
    // ALWAYS redirect referral links to landing page so clients see all solutions first
    if (refCode && !roleParam) {
      navigate(`/?ref=${refCode}`, { replace: true });
      return;
    }
    
    if (refCode) {
      setReferralCode(refCode);
      toast.success('Referral code applied!', {
        description: `You're signing up via referral code: ${refCode}`
      });
    }
    
    // Pre-select role if passed in URL
    if (roleParam === 'vacation_rental') {
      setFormData(prev => ({ ...prev, role: 'vacation_rental' }));
    } else if (roleParam === 'landlord') {
      setFormData(prev => ({ ...prev, role: 'landlord' }));
    } else if (roleParam === 'hoa') {
      setFormData(prev => ({ ...prev, role: 'hoa' }));
    } else if (roleParam === 'property_manager') {
      setFormData(prev => ({ ...prev, role: 'property_manager' }));
    }
  }, [searchParams, navigate]);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Map vacation_rental to landlord role for backend (vacation rentals are managed by landlord accounts)
      const isVacationRental = formData.role === 'vacation_rental';
      const backendRole = isVacationRental ? 'landlord' : formData.role;
      
      // Include referral code if present
      const registrationData = {
        ...formData,
        role: backendRole,
        ...(referralCode && { referral_code: referralCode })
      };

      const response = await axios.post(`${API}/auth/register`, registrationData);

      if (response.data.access_token) {
        localStorage.setItem('access_token', response.data.access_token);
        localStorage.setItem('user_id', response.data.user_id);
        localStorage.setItem('user_role', backendRole);
        
        toast.success('Account created successfully!', {
          description: referralCode 
            ? 'You were referred! Your referrer will earn rewards.' 
            : 'Welcome to Estate Flow!'
        });

        // Redirect to onboarding with vacation_rental pre-selected if applicable
        if (isVacationRental) {
          navigate('/onboarding?role=vacation_rental');
        } else {
          navigate('/onboarding');
        }
      }
    } catch (error) {
      console.error('Signup error:', error);
      toast.error('Signup failed', {
        description: error.response?.data?.detail || 'Please check your information and try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-emerald-50 flex items-center justify-center p-4">
      {/* Exit Intent Popup */}
      <ExitIntentPopup pageName="signup" />
      
      <Card className="w-full max-w-lg">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <Building2 className="h-12 w-12 text-emerald-600" />
          </div>
          <CardTitle className="text-2xl">Create Your Estate Flow Account</CardTitle>
          <CardDescription>
            Join the Leading Property Management Platform
          </CardDescription>
          
          {/* Notice for users looking to become referral partners - NEVER show when ref code is present */}
          {!hasReferralCode && (
            <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-sm text-amber-800 text-center">
                <strong>Looking to earn commissions?</strong>{' '}
                <button
                  type="button"
                  onClick={() => navigate('/become-partner')}
                  className="text-emerald-600 hover:underline font-semibold"
                >
                  Become a Referral Partner instead &rarr;
                </button>
              </p>
            </div>
          )}
          
          {hasReferralCode && (
            <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center justify-center">
              <Gift className="h-5 w-5 text-blue-600 mr-2" />
              <span className="text-sm font-medium text-blue-800">
                Signing up with referral code: <strong>{urlRefCode}</strong>
              </span>
            </div>
          )}
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="first_name">First Name</Label>
                <Input
                  id="first_name"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div>
                <Label htmlFor="last_name">Last Name</Label>
                <Input
                  id="last_name"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>

            <div>
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                required
              />
            </div>

            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleInputChange}
                required
                minLength={8}
              />
              <p className="text-xs text-gray-500 mt-1">Minimum 8 characters</p>
            </div>

            <div>
              <Label htmlFor="phone_number">Phone Number</Label>
              <Input
                id="phone_number"
                name="phone_number"
                type="tel"
                value={formData.phone_number}
                onChange={handleInputChange}
              />
            </div>

            <div>
              <Label htmlFor="role">I am a...</Label>
              <Select 
                value={formData.role} 
                onValueChange={(value) => setFormData({...formData, role: value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select your role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="landlord">Landlord</SelectItem>
                  <SelectItem value="property_manager">Property Manager</SelectItem>
                  <SelectItem value="hoa">HOA Administrator</SelectItem>
                  <SelectItem value="vacation_rental">Vacation Rental Manager</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="country">Country</Label>
              <Select 
                value={formData.country} 
                onValueChange={(value) => setFormData({...formData, country: value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select your country" />
                </SelectTrigger>
                <SelectContent>
                  {CARIBBEAN_COUNTRIES.map(country => (
                    <SelectItem key={country.code} value={country.code}>
                      {country.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {!referralCode && (
              <div>
                <Label htmlFor="referral_code_input">Referral Code (Optional)</Label>
                <Input
                  id="referral_code_input"
                  value={referralCode}
                  onChange={(e) => setReferralCode(e.target.value)}
                  placeholder="Enter referral code if you have one"
                />
              </div>
            )}

            <Button 
              type="submit" 
              className="w-full bg-emerald-600 hover:bg-emerald-700" 
              disabled={loading}
              size="lg"
            >
              {loading ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Creating Account...</>
              ) : (
                'Create Account'
              )}
            </Button>

            <div className="text-center text-sm text-gray-600">
              Already have an account?{' '}
              <button
                type="button"
                onClick={() => navigate('/landlord/login')}
                className="text-emerald-600 hover:underline font-medium"
              >
                Sign In
              </button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default SignupPage;
